import React from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import type { NodeDetail } from "../../../../types/api-types";
import { formatDetailValue } from "../link-chart-helpers";

interface DetailCardProps {
  detail: NodeDetail;
  isHovered: boolean;
  isCopied: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onCopy: (value: unknown, description: string) => void;
}

/**
 * Detail card component for displaying a single node detail with copy functionality
 */
const DetailCard: React.FC<DetailCardProps> = ({
  detail,
  isHovered,
  isCopied,
  onMouseEnter,
  onMouseLeave,
  onCopy,
}) => {
  return (
    <Card
      variant="outlined"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        backgroundColor: "background.paper",
        "&:hover": {
          boxShadow: 1,
        },
        position: "relative",
      }}
    >
      <CardContent sx={{ p: 1.5, pr: 5, "&:last-child": { pb: 1.5 } }}>
        <Typography
          variant="body2"
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            wordBreak: "break-word",
          }}
        >
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              flexShrink: 0,
            }}
          >
            {detail.description}:
          </Typography>
          <Typography
            component="span"
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "text.primary",
            }}
          >
            {formatDetailValue(detail.value)}
          </Typography>
        </Typography>
      </CardContent>
      {isHovered && (
        <Tooltip title={isCopied ? "Copied!" : "Copy"}>
          <IconButton
            size="small"
            onClick={() => onCopy(detail.value, detail.description)}
            sx={{
              position: "absolute",
              top: "50%",
              right: 8,
              transform: "translateY(-50%)",
              color: "primary.main",
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Card>
  );
};

export default DetailCard;
